// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Grid, IconButton, Link,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink } from 'react-router-dom';
import api from '../utils/api';
import { dateTime, dateUtc } from '../utils/dates';
import { DETECTOR_STATES } from '../utils/detecteur';
import { errorMessage } from '../utils/erreurs';

/**
 * État technique du parc des établissements : chaque établissement a-t-il un
 * serveur en ligne et une détection en marche, combien de chambres sont
 * déclarées ou couvertes. Aucune alerte n'y figure : celles d'un établissement
 * appartiennent à son équipe soignante, celles d'un foyer à sa famille.
 */

// Les trois états cassés d'un détecteur (detector_health.BROKEN_STATES).
const BROKEN_DETECTOR = new Set(['crashed', 'stopped', 'disabled']);
const REFRESH_INTERVAL_MS = 60 * 1000;

function ilYA(iso) {
  const d = dateUtc(iso);
  if (!d) return 'jamais';
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function Tile({ titre, valeur, detail, couleur: color }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography color="text.secondary" variant="body2" gutterBottom>{titre}</Typography>
        <Typography variant="h4" color={color || 'inherit'}>{valeur}</Typography>
        {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
      </CardContent>
    </Card>
  );
}

function Pastille({ enLigne, titre }) {
  return (
    <Tooltip title={titre || ''}>
      <Box sx={{
        width: 10, height: 10, borderRadius: '50%', display: 'inline-block', mr: 0.75,
        bgcolor: enLigne ? 'success.main' : 'text.disabled',
      }} />
    </Tooltip>
  );
}

// Le nom d'un établissement mène à sa fiche client quand on connaît son
// organisation ; sinon il reste du texte, jamais un lien mort.
function FacilityName({ site }) {
  const text = site.nom || 'Établissement inconnu';
  if (site.organisationId) {
    return (
      <Link component={RouterLink} to={`/clients/etablissement/${site.organisationId}`}
        underline="hover" variant="body2">
        {text}
      </Link>
    );
  }
  return <Typography variant="body2">{text}</Typography>;
}

export default function DashboardPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const charger = useCallback(async () => {
    try {
      const [kitsRes, roomsRes] = await Promise.all([
        api.get('/kits'),
        api.get('/properties/', { params: { scope: 'facility' } }),
      ]);
      // La liste des clients demande un droit que tout compte d'équipe n'a pas
      // (users_manage ou billing). Sans elle, l'écran reste utilisable : les
      // établissements se déduisent des serveurs.
      let organizations = [];
      let organizationsRejected = false;
      try {
        const res = await api.get('/pro/admin/organisations');
        organizations = Array.isArray(res.data) ? res.data : (res.data?.organisations || []);
      } catch (e) {
        if (e?.response?.status === 403) organizationsRejected = true;
        else throw e;
      }
      setData({
        // Un kit « retiré » n'est pas un serveur : des lignes anciennes en base
        // feraient compter plusieurs serveurs pour un seul posé.
        kits: (kitsRes.data?.kits || []).filter((k) => k.status !== 'retired'),
        chambres: Array.isArray(roomsRes.data) ? roomsRes.data : [],
        organisations: organizations,
        organisationsRefusees: organizationsRejected,
      });
      setError('');
      setUpdatedAt(new Date());
    } catch (e) {
      setError(errorMessage(e, 'Lecture du tableau de bord impossible'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    charger();
    const timer = setInterval(charger, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [charger]);

  // Les établissements : ceux du fichier client d'abord, complétés par ceux
  // qu'un serveur désigne et que le fichier ne porte pas (droit refusé, ou
  // incohérence à voir).
  const sites = useMemo(() => {
    if (!data) return [];
    const parId = new Map();
    for (const org of data.organisations) {
      for (const s of org.sites || []) {
        parId.set(s.id, {
          id: s.id, nom: s.name, ville: s.city, organisation: org.name,
          organisationId: org.id, chambres: s.rooms ?? null, actif: s.is_active !== false,
          // Chambres actives couvertes par une caméra, si le backend la rend ;
          // sinon null, jamais 0.
          couvertes: Number.isFinite(s.rooms_covered) ? s.rooms_covered : null,
        });
      }
    }
    for (const k of data.kits) {
      if (!k.site_id || parId.has(k.site_id)) continue;
      parId.set(k.site_id, {
        id: k.site_id, nom: k.site_name, ville: k.city, organisation: k.organisation_name,
        organisationId: k.organisation_id, chambres: null, actif: true, couvertes: null,
      });
    }
    // Les chambres comptées par le fichier client valent mieux (actives
    // seulement) ; à défaut, celles que la liste des chambres rattache au site.
    const roomsBySite = {};
    for (const c of data.chambres) {
      if (c.site_id && c.is_active !== false) roomsBySite[c.site_id] = (roomsBySite[c.site_id] || 0) + 1;
    }
    for (const s of parId.values()) {
      if (s.chambres == null) s.chambres = roomsBySite[s.id] || 0;
    }
    return [...parId.values()];
  }, [data]);

  const view = useMemo(() => {
    if (!data) return null;
    const serversBySite = {};
    for (const k of data.kits) {
      if (!k.site_id) continue;
      (serversBySite[k.site_id] = serversBySite[k.site_id] || []).push(k);
    }

    const rows = sites.map((s) => {
      const servers = serversBySite[s.id] || [];
      const enLigne = servers.filter((k) => k.detector?.box_online === true);
      const detection = servers.some((k) => k.detector?.detection_active === true);
      const casse = servers.find((k) => BROKEN_DETECTOR.has(k.detector?.detector_state));
      const detectorState = detection ? 'running'
        : casse ? casse.detector.detector_state
          : servers.length ? (servers[0].detector?.detector_state || 'unknown') : 'unknown';
      return {
        site: s, serveurs: servers, enLigne: enLigne.length, etatDetecteur: detectorState,
        raisonDetecteur: casse?.detector?.detector_reason || null,
      };
    }).sort((a, b) => {
      // Ce qui demande une action monte : serveur hors ligne ou détecteur cassé.
      const weight = (l) => ((l.serveurs.length && l.enLigne === 0) || BROKEN_DETECTOR.has(l.etatDetecteur) ? 1 : 0);
      return weight(b) - weight(a) || (a.site.nom || '').localeCompare(b.site.nom || '');
    });

    const servers = data.kits.filter((k) => k.site_id);
    return {
      sitesTotal: sites.length,
      sitesEquipes: sites.filter((s) => (serversBySite[s.id] || []).length > 0).length,
      serveursTotal: servers.length,
      serveursEnLigne: servers.filter((k) => k.detector?.box_online === true).length,
      detectionActive: servers.filter((k) => k.detector?.detection_active === true).length,
      detecteursCasses: servers.filter((k) => BROKEN_DETECTOR.has(k.detector?.detector_state)),
      chambres: sites.reduce((n, s) => n + (s.chambres || 0), 0),
      // La couverture n'est affichée que si chaque établissement la porte :
      // une somme partielle sous-estimerait les chambres couvertes.
      couvertes: sites.length && sites.every((s) => s.couvertes != null)
        ? sites.reduce((n, s) => n + s.couvertes, 0)
        : null,
      lignes: rows,
    };
  }, [data, sites]);

  if (loading && !view) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }

  const offlineServers = view ? view.lignes.filter((l) => l.serveurs.length && l.enLigne === 0) : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Parc des établissements</Typography>
          <Typography variant="body2" color="text.secondary">
            Serveurs et détection
            {updatedAt && ` · actualisé à ${updatedAt.toLocaleTimeString('fr-FR')}`}
          </Typography>
        </Box>
        <Tooltip title="Actualiser">
          <IconButton onClick={charger}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {view && (
        <>
          {/* En tête : établissement qui se croit surveillé sans l'être. */}
          {(offlineServers.length > 0 || view.detecteursCasses.length > 0) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {offlineServers.map((l) => (
                <div key={`hl-${l.site.id}`}>
                  <strong>{l.site.nom}</strong> : serveur hors ligne
                  {l.serveurs[0]?.last_seen ? `, dernier contact ${ilYA(l.serveurs[0].last_seen)}` : ', jamais vu'}
                </div>
              ))}
              {view.detecteursCasses.map((k) => (
                <div key={`dc-${k.id}`}>
                  <strong>{k.site_name || k.code}</strong> : {(DETECTOR_STATES[k.detector.detector_state] || DETECTOR_STATES.unknown).label.toLowerCase()}
                  {k.detector.detector_reason ? ` (${k.detector.detector_reason})` : ''}
                </div>
              ))}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Tile titre="Établissements surveillés" valeur={`${view.sitesEquipes}/${view.sitesTotal}`}
                detail={data.organisationsRefusees ? 'fichier client non lisible avec ce compte' : 'avec un serveur posé'} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Tile titre="Serveurs en ligne" valeur={`${view.serveursEnLigne}/${view.serveursTotal}`}
                couleur={view.serveursTotal && view.serveursEnLigne < view.serveursTotal ? 'error.main' : 'success.main'}
                detail="vus depuis moins de 10 min" />
            </Grid>
            <Grid item xs={6} md={3}>
              <Tile titre="Détection active" valeur={`${view.detectionActive}/${view.serveursTotal}`}
                couleur={view.detecteursCasses.length ? 'error.main' : (view.detectionActive ? 'success.main' : 'inherit')}
                detail={view.detecteursCasses.length ? `${view.detecteursCasses.length} détecteur(s) en défaut` : 'rapport de santé du serveur'} />
            </Grid>
            <Grid item xs={6} md={3}>
              {/* Sans rooms_covered, la tuile dit « déclarées » et non
                  « surveillées » : elle compte les chambres actives du fichier
                  client, alors que le serveur ne sert une chambre qu'avec une
                  caméra et un résident consentant. */}
              {view.couvertes != null ? (
                <Tile titre="Chambres couvertes" valeur={`${view.couvertes}/${view.chambres}`}
                  couleur={view.couvertes < view.chambres ? 'warning.main' : 'inherit'}
                  detail="chambres actives couvertes par une caméra, sur les déclarées" />
              ) : (
                <Tile titre="Chambres déclarées" valeur={view.chambres}
                  detail="actives au fichier client, couverture caméra non mesurée ici" />
              )}
            </Grid>
          </Grid>

          <Paper>
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography variant="h6">Par établissement</Typography>
            </Box>
            {view.lignes.length === 0 ? (
              <Typography sx={{ p: 2 }} color="text.secondary">Aucun établissement.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Établissement</TableCell>
                      <TableCell>Serveur</TableCell>
                      <TableCell>Détection</TableCell>
                      <TableCell align="right">Chambres déclarées</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {view.lignes.map((l) => {
                      const state = DETECTOR_STATES[l.etatDetecteur] || DETECTOR_STATES.unknown;
                      return (
                        <TableRow key={l.site.id} hover>
                          <TableCell>
                            <FacilityName site={l.site} />
                            <Typography variant="caption" color="text.secondary" display="block">
                              {[l.site.organisation, l.site.ville].filter(Boolean).join(' · ')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {l.serveurs.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">Aucun serveur</Typography>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Pastille enLigne={l.enLigne > 0}
                                  titre={l.serveurs.map((k) => `${k.code} : dernier contact ${dateTime(k.last_seen, 'jamais')}`).join(' ; ')} />
                                <Typography variant="body2">
                                  {l.enLigne > 0 ? 'En ligne' : `Hors ligne, ${ilYA(l.serveurs[0].last_seen)}`}
                                  {l.serveurs.length > 1 ? ` (${l.enLigne}/${l.serveurs.length})` : ''}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            {l.serveurs.length > 0 && (
                              <Tooltip title={l.raisonDetecteur || ''}>
                                <Chip size="small" label={state.label} color={state.couleur} />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right">{l.site.chambres}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}
